import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { useCreateStockItem, useUploadStockPhoto } from '../../hooks/useStock';
import { newIdempotencyKey } from '../../services/stockApi';
import { parseWholeNumber } from '../../utils/numberFormatter';
import { TASK_PHOTO_MAX_BYTES, TASK_PHOTO_MIME_TYPES } from '../../config/taskPhotoRules';
import { TEXT, UNIT_LABELS, errorText } from '../../config/stockLabels';

/**
 * «إضافة صنف» — name in both languages (O4), unit (fixed from here on, D16), the count on the shelf now
 * (recorded as the opening count, D5), an optional low-stock level and an optional photo (D14).
 *
 * One key per opening (D11): a double tap returns the first item instead of making two. The photo is
 * uploaded after the item exists; if that fails the item stays and the page says where to add it.
 */
const FORM_ID = 'stk-add-form';

const AddStockItemModal =({ show, onHide, storeId, units }) => {
  const createItem = useCreateStockItem();
  const uploadPhoto = useUploadStockPhoto();
  const fileRef = useRef(null);

  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [unit, setUnit] = useState('');
  const [opening, setOpening] = useState('');
  const [level, setLevel] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitKey, setSubmitKey] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show) return;
    setNameAr(''); setNameEn(''); setUnit(''); setOpening(''); setLevel('');
    setFile(null); setPreview(null); setError(null);
    setSubmitKey(newIdempotencyKey());
  }, [show]);

  // A preview URL lives only as long as its file.
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const pickPhoto = (e) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    if (!TASK_PHOTO_MIME_TYPES.includes(picked.type)) { setError(errorText({ response: { data: { code: 'STOCK_024' } } })); return; }
    if (picked.size > TASK_PHOTO_MAX_BYTES) { setError(errorText({ response: { data: { code: 'STOCK_023' } } })); return; }
    setError(null);
    setFile(picked);
    setPreview(URL.createObjectURL(picked));
  };

  const busy = createItem.isPending || uploadPhoto.isPending;

  const save = async (e) => {
    e.preventDefault();
    setError(null);
    if (!nameAr.trim() || !nameEn.trim()) { setError(errorText({ response: { data: { code: 'STOCK_004' } } })); return; }
    if (!unit) { setError(errorText({ response: { data: { code: 'STOCK_008' } } })); return; }
    const openingQuantity = parseWholeNumber(opening);
    if (openingQuantity === null) { setError(`${TEXT.openingQuantity}: ${TEXT.wholeNumber}`); return; }
    const lowStockLevel = level.trim() === '' ? null : parseWholeNumber(level);
    if (level.trim() !== '' && lowStockLevel === null) { setError(`${TEXT.lowStockLevel}: ${TEXT.wholeNumber}`); return; }

    let created;
    try {
      created = await createItem.mutateAsync({
        storeId, nameAr, nameEn, unit, openingQuantity, lowStockLevel, idempotencyKey: submitKey
      });
    } catch (err) {
      setError(errorText(err));
      return;
    }
    if (file && created?.item?._id) {
      try {
        await uploadPhoto.mutateAsync({ itemId: created.item._id, file });
      } catch (err) {
        setError(TEXT.photoFailedAfterCreate);
        return;
      }
    }
    onHide();
  };

  return (
    <Modal show={show} onHide={busy ? undefined : onHide} centered scrollable fullscreen="sm-down" dir="rtl" dialogClassName="stk-modal">
      <Modal.Header closeButton={!busy}>
        <Modal.Title className="stk-modal__title">
          <i className="bi bi-plus-square" aria-hidden="true"></i> {TEXT.addItem}
        </Modal.Title>
      </Modal.Header>
      {/* The form stays inside the body: Bootstrap's scrollable modal needs the body directly under the modal, or a
          long form is cut off with the buttons out of reach. The footer's button reaches the form by id. */}
      <Modal.Body>
        <Form id={FORM_ID} onSubmit={save} noValidate>
          <div className="stk-add__grid">
            <Form.Group className="stk-field" controlId="stk-add-ar">
              <Form.Label>{TEXT.nameAr}</Form.Label>
              <Form.Control value={nameAr} maxLength={80} required onChange={(e) => setNameAr(e.target.value)} />
            </Form.Group>
            <Form.Group className="stk-field" controlId="stk-add-en">
              <Form.Label>{TEXT.nameEn}</Form.Label>
              <Form.Control value={nameEn} maxLength={80} required dir="ltr" onChange={(e) => setNameEn(e.target.value)} />
            </Form.Group>
            <Form.Group className="stk-field" controlId="stk-add-unit">
              <Form.Label>{TEXT.unit}</Form.Label>
              <Form.Select value={unit} required onChange={(e) => setUnit(e.target.value)}>
                <option value="">—</option>
                {(units || []).map(code => <option key={code} value={code}>{UNIT_LABELS[code] || code}</option>)}
              </Form.Select>
              <Form.Text>{TEXT.unitLocked}</Form.Text>
            </Form.Group>
            <Form.Group className="stk-field" controlId="stk-add-opening">
              <Form.Label>{TEXT.openingQuantity}</Form.Label>
              <Form.Control value={opening} inputMode="numeric" autoComplete="off" required onChange={(e) => setOpening(e.target.value)} />
              <Form.Text>{TEXT.openingQuantityHint}</Form.Text>
            </Form.Group>
            <Form.Group className="stk-field" controlId="stk-add-level">
              <Form.Label>{TEXT.lowStockLevel}</Form.Label>
              <Form.Control value={level} inputMode="numeric" autoComplete="off" onChange={(e) => setLevel(e.target.value)} />
              <Form.Text>{TEXT.lowStockLevelHint}</Form.Text>
            </Form.Group>
          </div>

          <div className="stk-field stk-add__photo">
            <span className="form-label">{TEXT.photo}</span>
            <div className="stk-add__photo-row">
              <span className="stk-line__photo stk-add__preview" aria-hidden="true">
                {preview ? <img src={preview} alt="" /> : <i className="bi bi-image"></i>}
              </span>
              <input ref={fileRef} type="file" accept={TASK_PHOTO_MIME_TYPES.join(',')} hidden onChange={pickPhoto} />
              <Button variant="outline-primary" size="sm" onClick={() => fileRef.current?.click()} disabled={busy}>
                <i className="bi bi-camera" aria-hidden="true"></i> {file ? TEXT.changePhoto : TEXT.choosePhoto}
              </Button>
            </div>
            <Form.Text>{TEXT.photoHint}</Form.Text>
          </div>

          {error && <div className="stk-error" role="alert">{error}</div>}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={busy}>{TEXT.cancel}</Button>
        <Button variant="primary" type="submit" form={FORM_ID} disabled={busy}>
          {busy && <Spinner animation="border" size="sm" className="stk-spin" />}
          {uploadPhoto.isPending ? TEXT.uploadingPhoto : TEXT.addItem}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddStockItemModal;
